import React from 'react'
import { Chip, useTheme } from '@material-ui/core'

import { Container } from '../../../components'
import { FinderSelection } from '../../../hooks'
import { InventoryConfig } from '../../../models'
import {
    FlagInspectionFilter,
    AttachmentFilterValue,
    WorkorderFilterValue,
    HandledFilterValue,
} from './FlagInspectionFilterDrawer'

interface Props {
    filter: FlagInspectionFilter
    finderSelection: FinderSelection
    inventoryConfigList: InventoryConfig[]
    setFilter: (newValue: FlagInspectionFilter) => void
}

export const FlagInspectionFilterChips: React.FC<Props> = ({
    filter,
    finderSelection,
    inventoryConfigList,
    setFilter,
}) => {
    const theme = useTheme()

    const chipStyle: React.CSSProperties = {
        marginRight: theme.spacing(1),
        marginBottom: theme.spacing(0.5),
        fontWeight: 500,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
    }

    const chips: JSX.Element[] = []

    // Inventory Config Chip
    if (filter.inventoryConfigId !== -1) {
        const invConfig = inventoryConfigList.find(
            (inv) => inv.id === filter.inventoryConfigId,
        )
        if (invConfig) {
            chips.push(
                <Chip
                    key="inv-config"
                    size="small"
                    style={chipStyle}
                    label={invConfig.name}
                    onDelete={() => {
                        setFilter({
                            ...filter,
                            inventoryConfigId: -1,
                            modified: true,
                        })
                    }}
                />,
            )
        }
    }

    // Attachments Chip
    if (filter.attachmentFilter === AttachmentFilterValue.HAS_ATTACHMENTS) {
        chips.push(
            <Chip
                key="attachments"
                size="small"
                style={chipStyle}
                label="Has Attachments"
                onDelete={() => {
                    setFilter({
                        ...filter,
                        attachmentFilter: AttachmentFilterValue.NA,
                        modified: true,
                    })
                }}
            />,
        )
    } else if (
        filter.attachmentFilter === AttachmentFilterValue.NO_ATTACHMENTS
    ) {
        chips.push(
            <Chip
                key="attachments"
                size="small"
                style={chipStyle}
                label="No Attachments"
                onDelete={() => {
                    setFilter({
                        ...filter,
                        attachmentFilter: AttachmentFilterValue.NA,
                        modified: true,
                    })
                }}
            />,
        )
    }

    // Workorders Chip
    if (filter.workorderFilter === WorkorderFilterValue.HAS_WORKORDERS) {
        chips.push(
            <Chip
                key="workorders"
                size="small"
                style={chipStyle}
                label="Has Workorders"
                onDelete={() => {
                    setFilter({
                        ...filter,
                        workorderFilter: WorkorderFilterValue.NA,
                        modified: true,
                    })
                }}
            />,
        )
    } else if (filter.workorderFilter === WorkorderFilterValue.NO_WORKORDERS) {
        chips.push(
            <Chip
                key="workorders"
                size="small"
                style={chipStyle}
                label="No Workorders"
                onDelete={() => {
                    setFilter({
                        ...filter,
                        workorderFilter: WorkorderFilterValue.NA,
                        modified: true,
                    })
                }}
            />,
        )
    }

    // Handled Chip
    if (filter.handledFilter === HandledFilterValue.HANDLED) {
        chips.push(
            <Chip
                key="handled"
                size="small"
                style={chipStyle}
                label="Handled"
                onDelete={() => {
                    setFilter({
                        ...filter,
                        handledFilter: HandledFilterValue.NA,
                        modified: true,
                    })
                }}
            />,
        )
    } else if (filter.handledFilter === HandledFilterValue.NOT_HANDLED) {
        chips.push(
            <Chip
                key="handled"
                size="small"
                style={chipStyle}
                label="Not Handled"
                onDelete={() => {
                    setFilter({
                        ...filter,
                        handledFilter: HandledFilterValue.NA,
                        modified: true,
                    })
                }}
            />,
        )
    }

    // Location Chips - Units
    const unitCount = finderSelection.selection.unit.length
    if (unitCount > 0) {
        if (unitCount <= 3) {
            Object.keys(finderSelection.selection.unit).forEach((strKey) => {
                if (strKey === 'length') return

                const numKey = parseInt(strKey)
                if (isNaN(numKey)) return

                const unit = finderSelection.selection.unit[numKey]
                if (!unit) return

                chips.push(
                    <Chip
                        key={`unit-${numKey}`}
                        size="small"
                        style={chipStyle}
                        label={unit.name}
                        onDelete={() => {
                            finderSelection.setFinderSelection({
                                type: 'unit',
                                location: unit,
                            })
                            setFilter({ ...filter, modified: true })
                        }}
                    />,
                )
            })
        } else {
            chips.push(
                <Chip
                    key="units-count"
                    size="small"
                    style={chipStyle}
                    label={`${unitCount} Units`}
                    onDelete={() => {
                        finderSelection.setFinderSelection(undefined)
                        setFilter({ ...filter, modified: true })
                    }}
                />,
            )
        }
    }

    // Location Chips - Folders
    const folderCount = finderSelection.selection.folder.length
    if (folderCount > 0) {
        if (folderCount <= 2) {
            Object.keys(finderSelection.selection.folder).forEach((strKey) => {
                if (strKey === 'length') return

                const numKey = parseInt(strKey)
                if (isNaN(numKey)) return

                const folder = finderSelection.selection.folder[numKey]
                if (!folder) return

                chips.push(
                    <Chip
                        key={`folder-${numKey}`}
                        size="small"
                        style={chipStyle}
                        label={folder.name}
                        onDelete={() => {
                            finderSelection.setFinderSelection({
                                type: 'folder',
                                location: folder,
                            })
                            setFilter({ ...filter, modified: true })
                        }}
                    />,
                )
            })
        } else {
            chips.push(
                <Chip
                    key="folders-count"
                    size="small"
                    style={chipStyle}
                    label={`${folderCount} Folders`}
                    onDelete={() => {
                        finderSelection.setFinderSelection(undefined)
                        setFilter({ ...filter, modified: true })
                    }}
                />,
            )
        }
    }

    if (chips.length === 0) {
        return null
    }

    return (
        <Container
            style={{
                flexWrap: 'wrap',
                alignItems: 'center',
                marginBottom: theme.spacing(1),
            }}
        >
            {chips}
        </Container>
    )
}
